import React, { useMemo } from 'react';
import { Alert } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import { isEqual } from 'lodash';
import ManagedInput from 'components/form/managed_input';
import ToggleOverrideSection from '../shared/toggle_override_section';
import { ALLOWED_COUNTRIES } from './countries';

interface CountryPickerProps {
  defaultCountryIds?: number[] | null;
  selectAllCountryIds?: boolean;
  selectedCountryIds?: number[] | null;
  onUpdateCountry: (payload: {
    countryIds?: number[],
    selectAllCountryIds?: boolean
  }) => void;
}

export default function CountryPicker({
  defaultCountryIds,
  selectAllCountryIds,
  selectedCountryIds = null,
  onUpdateCountry
}: CountryPickerProps) {
  const currentCountryIds = selectedCountryIds || [];

  const handleUpdateCountry = (countryId: number, value: boolean) => {
    const newCountryIds = value
      ? [...currentCountryIds, countryId]
      : currentCountryIds.filter(id => id !== countryId);

    onUpdateCountry({
      countryIds: newCountryIds
    });
  };

  const handleSetDefaults = () => {
    onUpdateCountry({
      selectAllCountryIds: false,
      countryIds: defaultCountryIds || []
    });
  };

  const canSetDefault: boolean = useMemo(() => {
    const hasDefaults = !!defaultCountryIds && defaultCountryIds.length > 0;
    const countriesChanged = hasDefaults &&
      !selectAllCountryIds &&
      !isEqual(defaultCountryIds?.sort(), selectedCountryIds?.sort());
    return countriesChanged;
  }, [defaultCountryIds, selectedCountryIds, selectAllCountryIds]);

  const renderDefaultAlert = () => {
    if (!canSetDefault) return null;

    return (
      <Alert className="text-center" variant="info">
        <div>
          The country selection has been edited from the initial configuration.
          Selecting &quot;revert&quot; below will undo those changes,
          and restore the countries selected when this template was created.
        </div>
        <div className="btn btn-ghost btn-sm mt-1" onClick={handleSetDefaults}>
          <FontAwesomeIcon icon={faUndo} />
          Revert Countries
        </div>
      </Alert>
    );
  };

  return (
    <React.Fragment>
      <ToggleOverrideSection
        checked={selectAllCountryIds}
        name="country-override"
        subtitle="Unchecking will allow you to select specific countries for this document template"
        title="Enable for all countries"
        onOverride={handleSetDefaults}
        onRemove={() => onUpdateCountry({ selectAllCountryIds: true })}
      />
      {renderDefaultAlert()}
      {!selectAllCountryIds && (
        <div className="d-flex flex-column gap-2">
          {ALLOWED_COUNTRIES.map(country => (
            <ManagedInput
              checked={currentCountryIds.includes(country.id)}
              key={country.id}
              label={country.label}
              name={`country-${country.id}`}
              type="checkbox"
              onValueChange={(value: boolean) => handleUpdateCountry(country.id, value)}
            >
              {country.label}
            </ManagedInput>
          ))}
        </div>
      )}
    </React.Fragment>
  );
}
